// AiMarker — the ONE consistent "this is AI" marker (ai-transparency.md §3),
// pure. Learned once, applied uniformly across every surface where AI-authored
// content shows up (material header, proposal cards, AI-placed elements, the
// assistant thread).
//
// The atom, two channels ALWAYS (never colour alone, never glyph alone):
//   • a `sparkle` glyph — the single cross-industry AI symbol (unified from the
//     old star/sparkle split), decorative (`aria-hidden`);
//   • a WORD — the visible label AND the screen-reader announcement;
//   • the reserved `--color-agent` token (== the accent ember) — a calm accent,
//     never red/warning, and never the sole differentiator.
//
// It is a pure PROJECTION of recorded provenance: the caller renders it because
// it read `Authorship === "agent"` / `Actor.type === "actor_agent"` from the
// truth already in the log. The marker carries no boolean of its own; absence of
// the marker IS "human" (never falsely brand human work as AI —
// ai-transparency §5). Mark GENERATION, not assistance, and at the right
// altitude (once per artifact / element / thread — §4), never per bubble.
//
// a11y: `role="note"` + an aria-label carrying the full phrase, so a short
// visible "AI" still announces "AI-generated" without sight or hover — the
// shipped HealthIndicator pattern (icon + word + role, colour never sole
// channel).

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import "./AiMarker.css";

// Three forms of the ONE atom (ai-transparency §3): the whole-artifact BADGE,
// the element-level CHIP, and the chat MESSAGE-TAG. Same glyph, same colour,
// same a11y — only size/weight differ.
export type AiMarkerVariant = "badge" | "chip" | "tag";

export type AiMarkerProps = {
    // Which form. Default `chip` (the element/inline register).
    readonly variant?: AiMarkerVariant;
    // The visible word. Defaults per variant: the badge states "Made with AI",
    // the chip/tag the short "AI". The caller may override (localised upstream).
    readonly label?: string;
    // Icon-only: hide the visible word, keep just the glyph (the a11y name +
    // role stay, so it still announces "AI-generated"). Use where the meaning is
    // taught elsewhere (e.g. onboarding) and the surface wants the quietest mark.
    readonly iconOnly?: boolean;
    // The on-demand provenance detail (the "About this result" layer) — a
    // tooltip, never a modal or a blocking warning.
    readonly title?: string;
    readonly className?: string;
};

export const AiMarker = ({
    variant = "chip",
    label,
    iconOnly,
    title,
    className,
}: AiMarkerProps): ReactNode => {
    const { t } = useLingui();
    const word = label ?? (variant === "badge" ? t`Made with AI` : t`AI`);
    return (
        <span
            className={`bilda-ai-marker bilda-ai-marker--${variant}${
                className ? ` ${className}` : ""
            }`}
            role="note"
            aria-label={t`AI-generated`}
            title={title}
        >
            <span className="bilda-ai-marker__glyph" aria-hidden="true">
                <Icon name="sparkle" size="xs" />
            </span>
            {iconOnly ? null : (
                <span className="bilda-ai-marker__label">{word}</span>
            )}
        </span>
    );
};
